import React from 'react';
import { HealthArticle, HealthCheckDefinition } from '../../../../services/performanceMetricsService';
import { Sparkline } from '../Performance/Sparkline';
import { fmt, fmtPos, fmtDate, decodeHtml, isClicksDeclining, severityColor, severityBg } from './healthCheckHelpers';

export const ArticleCard: React.FC<{
  article: HealthArticle;
  rank?: number;
  checkLookup: Record<string, HealthCheckDefinition>;
  onStartRefresh?: (article: HealthArticle) => void;
  onViewDetails?: () => void;
  isStartingRefresh?: boolean;
}> = ({ article, rank, checkLookup, onStartRefresh, onViewDetails, isStartingRefresh }) => {
  const failingChecks = article.checks.filter((c) => c.severity > 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white">
        <div className="flex items-start gap-3">
          {rank != null ? (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500">
              {rank}
            </div>
          ) : (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-sm font-bold text-red-600">
              !
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-900 hover:text-blue-600 truncate"
              >
                {decodeHtml(article.title)}
              </a>
              <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium shrink-0">
                Score: {article.total_score}
              </span>
              {article.data_quality === 'insufficient' && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 shrink-0" title={`Only ${article.data_points} days of data. Some checks were skipped.`}>
                  {article.data_points}d data
                </span>
              )}
              {article.data_quality === 'no_data' && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                  No GSC data
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400 mt-0.5 truncate">{article.url}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {article.category && <>{article.category} · </>}
              Published {fmtDate(article.published_at)}
              {article.word_count > 0 && <> · {article.word_count.toLocaleString()} words</>}
            </div>
          </div>

          {/* Start Refresh + Mini metrics */}
          <div className="shrink-0 flex items-center gap-4 text-right">
            {onViewDetails && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
                className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
              >
                View Details
              </button>
            )}
            {onStartRefresh && (
              <button
                onClick={(e) => { e.stopPropagation(); onStartRefresh(article); }}
                disabled={isStartingRefresh}
                className="text-xs px-2.5 py-1 rounded border border-cyan-300 text-cyan-700 hover:bg-cyan-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1.5"
              >
                {isStartingRefresh && <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-cyan-500" />}
                {isStartingRefresh ? 'Starting...' : 'Start Refresh'}
              </button>
            )}
            <div>
              <Sparkline
                values={article.clicks_series}
                width={80}
                height={20}
                strokeColor={isClicksDeclining(article) ? '#ef4444' : '#01bcd4'}
                fillColor={isClicksDeclining(article) ? 'rgba(239, 68, 68, 0.12)' : 'rgba(1, 188, 212, 0.15)'}
              />
            </div>
            <div className="text-xs">
              <div className="text-gray-900 font-medium tabular-nums">{fmt(article.clicks)} clicks</div>
              <div className="text-gray-500 tabular-nums">{fmt(article.impressions)} imp</div>
              <div className="text-gray-500 tabular-nums">pos {fmtPos(article.avg_position)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Failing checks */}
      {failingChecks.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {failingChecks.map((c) => {
              const def = checkLookup[c.check_id];
              return (
                <div
                  key={c.check_id}
                  className={`text-xs px-2 py-1 rounded border ${severityBg(c.severity)}`}
                  title={c.detail}
                >
                  <span className={`font-medium ${severityColor(c.severity)}`}>
                    {def?.name || c.check_id}
                  </span>
                  <span className="text-gray-400 ml-1">({c.severity}/10)</span>
                </div>
              );
            })}
          </div>
          {/* Detail text for each failing check */}
          <div className="mt-2 space-y-1">
            {failingChecks.map((c) => c.detail && (
              <div key={c.check_id} className="text-xs text-gray-600">
                <span className={`font-medium ${severityColor(c.severity)}`}>
                  {checkLookup[c.check_id]?.name}:
                </span>{' '}
                {c.detail}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Opus diagnosis */}
      {article.diagnosis && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
          <div className="text-xs font-medium text-blue-800 mb-1">AI Diagnosis</div>
          <p className="text-sm text-blue-900 leading-relaxed">{article.diagnosis}</p>
        </div>
      )}
    </div>
  );
};
